import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { take, forkJoin, of, finalize, catchError } from 'rxjs';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

export interface IconSet {
  name: string;
  registerName: string;
  icons: string[];
}

@Injectable()
export class SvgService {
  private http = inject(HttpClient);

  // Use a signal for reactive icon sets
  icons = signal<IconSet[]>([]);

  // Loading state signal to track when icons are being loaded
  loading = signal(false);

  // Define SVG icon sets including standard and custom icons
  svgFonts = [
    {
      name: 'Feather',
      registerName: 'feather',
      url: 'assets/icons/feather.svg',
    },
    {
      name: 'Heroicons Solid',
      registerName: 'heroicons_solid',
      url: 'assets/icons/heroicons-solid.svg',
    },
    {
      name: 'Heroicons Outline',
      registerName: 'heroicons_outline',
      url: 'assets/icons/heroicons-outline.svg',
    },
    {
      name: 'Material Outline',
      registerName: 'mat_outline',
      url: 'assets/icons/material-outline.svg',
    },
  ];

  // Custom icons that are individually registered (not part of a set)
  customIcons = [
    {
      name: 'Pallet',
      registerName: '', // Default namespace
      iconId: 'pallet',
    },
    {
      name: 'Coins',
      registerName: '', // Default namespace
      iconId: 'coins',
    },
  ];

  /**
   * Loads all SVG icons from the defined icon sets and adds custom icons
   */
  loadSvgIcons(): void {
    this.loading.set(true);

    // Create an array to hold all requests
    const requests = this.svgFonts.map(font =>
      this.http.get(font.url, { responseType: 'text' }).pipe(
        take(1),
        catchError(err => {
          console.error(`Error loading icon set "${font.name}":`, err);
          return of('');
        }),
        finalize(() => this.addCustomIcons())
      )
    );

    // Use forkJoin to wait for all requests to complete
    forkJoin(requests).subscribe({
      next: responses => {
        // Process each response
        const iconSets = responses.map((svgText, index) => {
          const font = this.svgFonts[index];
          return {
            name: font.name,
            registerName: font.registerName,
            icons: this.parseSvg(svgText),
          };
        });

        // Update the icons signal with all sets
        this.icons.set(iconSets);
      },
      error: err => {
        console.error('Error loading SVG icons:', err);
        this.loading.set(false);
      },
    });
  }

  /**
   * Adds custom individually registered icons to the icons collection
   */
  private addCustomIcons(): void {
    if (this.customIcons.length > 0) {
      // Create a custom icon set
      const customSet: IconSet = {
        name: 'Custom Icons',
        registerName: '', // Default namespace
        icons: this.customIcons.map(icon => icon.iconId),
      };

      // Add the custom icon set to our collection
      this.icons.update(currentIcons => [...currentIcons, customSet]);
      this.loading.set(false);
    }
  }

  /**
   * Parses an SVG string to extract icon IDs
   * @param svgText The SVG content as string
   * @returns Array of icon IDs
   */
  private parseSvg(svgText: string): string[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    let iconNames: string[] = [];

    // Try to get symbols first
    const symbols = doc.querySelectorAll('symbol');
    if (symbols.length > 0) {
      iconNames = Array.from(symbols).map(symbol => symbol.id);
    } else {
      // If no symbols, try to get SVGs with IDs
      const svgs = doc.querySelectorAll('svg');
      iconNames = Array.from(svgs)
        .map(svg => svg.getAttribute('id') || svg.getAttribute('data-icon'))
        .filter(Boolean) as string[];
    }

    // Filter out any null, undefined or empty strings
    return iconNames.filter(icon => icon !== null && icon !== undefined && icon !== '');
  }
}
